import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import axios from 'axios';
import { serializeAxiosError } from './../../shared/reducers/reducer.utils';

import { StatusFilter } from 'app/shared/model/enumerations/status-filter.model';
import { ITruckImplementDataSummary } from 'app/shared/model/truck-implement-data-summary.model';
import { defaultValue, ITruckImplement, TruckImplementListDTO } from 'app/shared/model/truck-implement.model';
import { createEntitySlice, EntityState, IQueryParams } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { IRepresentativeCompanyAndTruck } from 'app/shared/model/dto/representative-company-and-truck.model';

const initialState: EntityState<ITruckImplement> = {
  loading: false,
  loadingList: false,
  errorMessage: null,
  entities: [],
  entitiesAlt: [] as IRepresentativeCompanyAndTruck[],
  entitiesAlt2: [] as TruckImplementListDTO[],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/truck-implements';
// Create a cancel token for the request
let cancelTokenSource = axios.CancelToken.source();

// Actions

export const getEntities = createAsyncThunk('truckImplement/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<ITruckImplement[]>(requestUrl);
});

interface IQueryParamsSearch extends IQueryParams {
  companyId?: number;
  searchCompanyName?: string;
  searchLicensePlate?: string;
  searchDescription?: string;
  loading?: boolean;
  status?: StatusFilter | string;
}

export const getAllByCompanyIdAndLicensePlate = createAsyncThunk(
  'truckImplement/fetch_entity_list_search',
  async ({ page, size, sort, searchCompanyName, searchLicensePlate, searchDescription, loading, status }: IQueryParamsSearch) => {
    const company = `&searchCompanyName=${encodeURIComponent(searchCompanyName)}`;
    const licensePlate = `&searchLicensePlate=${encodeURIComponent(searchLicensePlate)}`;
    const description = `&searchDescription=${encodeURIComponent(searchDescription)}`;
    const requestUrl = `${apiUrl}/search?page=${page}&size=${size}&sort=${sort}${company}${licensePlate}${description}&status=${status}&cacheBuster=${new Date().getTime()}`;

    if (cancelTokenSource && loading) {
      cancelTokenSource.cancel('Request canceled, because a new request was made.');
    }

    cancelTokenSource = axios.CancelToken.source();

    try {
      return await axios.get<ITruckImplement[]>(requestUrl, { cancelToken: cancelTokenSource.token });
    } catch (error) {
      if (axios.isCancel(error)) {
        console.error('Canceled request: ', error.message);
      } else {
        throw error;
      }
    }
  }
);

export const getTruckImplementsWithoutPagination = createAsyncThunk('truckImplement/fetch_entity_list', async () => {
  const requestUrl = 'api/truck-implements-without-pagination';
  return axios.get<ITruckImplement[]>(requestUrl);
});

export const getEntitiesFiltered = createAsyncThunk(
  'truckImplement/fetch_entity_list',
  async (plateSearch: string) => {
    const requestUrl = `${apiUrl}${plateSearch ? `?plateSearch=${plateSearch.toUpperCase()}` : ''}`;
    return axios.get<ITruckImplement>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getEntity = createAsyncThunk(
  'truckImplement/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ITruckImplement>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);
export const getTruckImplementDataSummary = createAsyncThunk(
  'truckImplement/fetch_data_summary',
  async (truckImplementId: string | number) => {
    return axios.get<ITruckImplementDataSummary>(`${apiUrl}/data-summary/${truckImplementId}`);
  },
  {
    serializeError: serializeAxiosError,
  }
);
interface IPagedTruckImplementQueryParams extends IQueryParams {
  plateSearch?: string;
  companySearch?: string;
}
export const getDataSummaryAllTruckImplements = createAsyncThunk(
  'truckImplement/fetch_data_summary_all',
  async ({ page, size, sort, plateSearch, companySearch }: IPagedTruckImplementQueryParams) => {
    const onPlateSearch = `&plateSearch=${plateSearch?.toUpperCase() ?? ''}`;
    const onCompanySearch = `&companySearch=${companySearch?.toUpperCase() ?? ''}`;
    const requestUrl = `${apiUrl}/data-summary?${
      sort ? `page=${page}&size=${size}&sorted=${sort}${onPlateSearch}${onCompanySearch}&` : ''
    }cacheBuster=${new Date().getTime()}`;
    return axios.get<ITruckImplementDataSummary[]>(requestUrl);
  },
  {
    serializeError: serializeAxiosError,
  }
);

export const getTruckImplementList = createAsyncThunk(
  'truckImplementList/fetch_entity_list_dto',
  async ({ searchCompanyName, searchLicensePlate }: IQueryParamsSearch) => {
    const params = new URLSearchParams();

    if (searchCompanyName) params.append('searchCompanyName', searchCompanyName);
    if (searchLicensePlate) params.append('searchLicensePlate', searchLicensePlate);

    const url = params.toString() ? `${apiUrl}/list?${params.toString()}` : `${apiUrl}/list`;
    const response = await axios.get<TruckImplementListDTO[]>(url);

    return response.data;
  }
);

export const getAllTruckImplementList = createAsyncThunk(
  'truckImplementList/fetch_all_entity_list_dto',
  async ({ searchCompanyName, searchLicensePlate, companyId }: IQueryParamsSearch) => {
    const params = new URLSearchParams();

    if (companyId != null) params.append('companyId', String(companyId));
    if (searchCompanyName) params.append('searchCompanyName', searchCompanyName);
    if (searchLicensePlate) params.append('searchLicensePlate', searchLicensePlate);

    const url = params.toString() ? `${apiUrl}/list-all?${params.toString()}` : `${apiUrl}/list-all`;
    const response = await axios.get<TruckImplementListDTO[]>(url);

    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const getTruckListForGroup = createAsyncThunk(
  'truckImplement/fetch_list_for_group',
  async (companyId?: number) => {
    const companyParam = companyId ? `?companyId=${companyId}` : '';
    const response = await axios.get<TruckImplementListDTO[]>(`${apiUrl}/list-for-group${companyParam}`);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'truckImplement/create_entity',
  async (entity: ITruckImplement, thunkAPI) => {
    const result = await axios.post<ITruckImplement>(apiUrl, cleanEntity(entity));
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'truckImplement/update_entity',
  async (entity: ITruckImplement, thunkAPI) => {
    const result = await axios.put<ITruckImplement>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'truckImplement/partial_update_entity',
  async (entity: ITruckImplement, thunkAPI) => {
    const result = await axios.patch<ITruckImplement>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'truckImplement/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<ITruckImplement>(requestUrl);
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getAllTruckImplementsByCompanyType = createAsyncThunk(
  'truckImplement/fetch_entity_list',
  async () => {
    const requestUrl = `${apiUrl}/by-company-type`;
    return axios.get<IRepresentativeCompanyAndTruck[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

// slice

export const TruckImplementSlice = createEntitySlice({
  name: 'truckImplement',
  initialState,
  reducers: {
    reset(state) {
      state.updateSuccess = false;
      state.loading = false;
      state.entity = defaultValue;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addCase(getTruckImplementDataSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getTruckImplementList.pending, state => {
        state.loading = true;
      })
      .addCase(getTruckImplementList.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = action.payload;
      })
      .addCase(getTruckListForGroup.pending, state => {
        state.loading = true;
      })
      .addCase(getTruckListForGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.entitiesAlt2 = action.payload;
      })
      .addCase(getTruckImplementList.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message ?? 'Erro ao buscar lista de implementos';
      })
      .addCase(getAllTruckImplementList.pending, state => {
        state.loading = true;
      })
      .addCase(getAllTruckImplementList.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = action.payload;
      })
      .addCase(getAllTruckImplementList.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message ?? 'Erro ao buscar lista de implementos';
      })
      .addMatcher(isFulfilled(getDataSummaryAllTruckImplements), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getAllTruckImplementsByCompanyType), (state, action) => {
        return {
          ...state,
          loading: false,
          entitiesAlt: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(getAllByCompanyIdAndLicensePlate), (state, action) => {
        return {
          ...state,
          loadingList: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(
        isPending(
          getAllTruckImplementsByCompanyType,
          getEntities,
          getEntity,
          getTruckImplementDataSummary,
          getDataSummaryAllTruckImplements,
          getTruckImplementList,
          getAllTruckImplementList
        ),
        state => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.loading = true;
        }
      )
      .addMatcher(isPending(getAllByCompanyIdAndLicensePlate), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loadingList = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = TruckImplementSlice.actions;

// Reducer
export default TruckImplementSlice.reducer;
